import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_MANAGE_PROFILE_SOP,
  PAGE_ROUTE_SOPS
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { FORGOT_PASSWORD_SOP_SECTIONS } from "./constants/forgotPasswordSopContent";
import { FORGOT_PASSWORD_SOP_IMAGES } from "./constants/forgotPasswordSopImages";
import type { ForgotPasswordSopImageConfig } from "./types";
import {
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";

const ForgotPasswordSopImage: React.FC<{
  image: ForgotPasswordSopImageConfig;
  testId: string;
}> = ({ image, testId }) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={userGuideSectionImageSx}
    />
  );
};

const ForgotPasswordSopPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const runBooksPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_SOPS, language, defaultLang)
      : `/${PAGE_ROUTE_SOPS}`;
  const manageProfilePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_MANAGE_PROFILE_SOP, language, defaultLang)
      : `/${PAGE_ROUTE_MANAGE_PROFILE_SOP}`;
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="forgot-password-sop-page"
        eyebrow={t("forgot-password-sop-page-eyebrow", "Run books")}
        title={t(
          "forgot-password-sop-page-title",
          "How to reset a forgotten password"
        )}
        intro={t(
          "forgot-password-sop-page-intro",
          "Get back into your account when you cannot sign in. Request a reset by email, set a new password, then log in again."
        )}
      />

      <PublicInfoCallout testId="forgot-password-sop-page-note">
        {t(
          "forgot-password-sop-page-note",
          "Use the email linked to your account. After you update your password, return to the login page and sign in with the new password."
        )}
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        {FORGOT_PASSWORD_SOP_SECTIONS.map((section, sectionIndex) => (
          <Box
            key={section.id}
            component="section"
            sx={publicInfoGuideSectionSx}
            data-testid={`forgot-password-sop-section-${section.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
              {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
            </Typography>
            {section.introKey != null ? (
              <Typography
                variant="body1"
                sx={{ ...publicInfoBodySx, mb: 2 }}
                data-testid={`forgot-password-sop-intro-${section.id}`}
              >
                {t(section.introKey, section.introFallback ?? "")}
              </Typography>
            ) : null}
            <Box component="ol" sx={publicInfoOrderedListSx}>
              {section.steps.map((step, stepIndex) => {
                const image =
                  step.imageId != null
                    ? FORGOT_PASSWORD_SOP_IMAGES[step.imageId]
                    : undefined;

                return (
                  <Box key={`${section.id}-${stepIndex}`} component="li">
                    <Typography
                      component="span"
                      variant="body1"
                      sx={publicInfoBodySx}
                    >
                      {t(step.bodyKey, step.bodyFallback)}
                    </Typography>
                    {image ? (
                      <ForgotPasswordSopImage
                        image={image}
                        testId={`forgot-password-sop-image-${step.imageId}`}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t("forgot-password-sop-page-cta-title", "Already signed in?")}
        testId="forgot-password-sop-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "forgot-password-sop-page-cta-body",
            "If you can still access your account, change your password from Profile → Password instead."
          )}{" "}
          <MuiLink
            component={Link}
            to={runBooksPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("forgot-password-sop-page-cta-all-guides-link", "Run books")}
          </MuiLink>
          {t("forgot-password-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={manageProfilePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "forgot-password-sop-page-cta-profile-link",
              "How to manage your personal profile"
            )}
          </MuiLink>
          {t("forgot-password-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("forgot-password-sop-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("forgot-password-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("forgot-password-sop-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default ForgotPasswordSopPage;
